import React from "react";
import { MapPin, Clock, FileCheck2, Check, X, Eye, Receipt, AlertTriangle, RotateCcw, User } from "lucide-react";
import { fromNow, formatDate } from "../userManagement/shared";
import { BusinessAvatar } from "../userManagement/ServiceProvider";
import { isLicenseExpired, licenseExpiresSoon } from "./applicationsStore";

export const APP_STATUS = {
  pending: { label: "Pending review", badge: "bg-[#FEF3C7] text-[#B45309]", dot: "#F59E0B", bar: "#F59E0B" },
  approved: { label: "Approved", badge: "bg-[#DCFCE7] text-[#16A34A]", dot: "#16A34A", bar: "#16A34A" },
  rejected: { label: "Rejected", badge: "bg-[#FDECEA] text-[#EA352B]", dot: "#EA352B", bar: "#EA352B" },
};

export function AppStatusBadge({ status }) {
  const s = APP_STATUS[status] || APP_STATUS.pending;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold whitespace-nowrap ${s.badge}`}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.dot }} /> {s.label}
    </span>
  );
}

const ApplicationCard = ({ data, onView, onApprove, onReject }) => {
  const status = String(data.status || "pending").toLowerCase();
  const expired = isLicenseExpired(data);
  const expiringSoon = licenseExpiresSoon(data);

  return (
    <article className="group relative flex flex-col h-full rounded-2xl border border-[#EEEEF1] bg-[#FAFAFB] hover:bg-[#fff] hover:border-[#F6C9C5] hover:shadow-[0_10px_28px_rgba(234,53,43,0.08)] hover:-translate-y-0.5 transition-all overflow-hidden">
      <span className="absolute top-0 left-0 right-0 h-1" style={{ background: (APP_STATUS[status] || APP_STATUS.pending).bar }} />

      <button type="button" onClick={onView} className="text-left p-4 pt-5 flex-1">
        {/* Identity */}
        <div className="flex items-start gap-3">
          <BusinessAvatar name={data.title} size={46} />
          <div className="min-w-0 flex-1">
            <h3 className="m-0 text-[15px] font-semibold text-[#111] truncate group-hover:text-[#EA352B] transition-colors">{data.title}</h3>
            <p className="m-0 mt-0.5 text-xs text-[#6B7280] flex items-center gap-1 truncate">
              <User size={12} className="shrink-0" /> {data.owner} · {data.position}
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 mt-3">
          <AppStatusBadge status={status} />
          <span className="text-[11px] text-[#9CA3AF] inline-flex items-center gap-1" title={formatDate(data.submittedAt, true)}>
            <Clock size={11} /> {fromNow(data.submittedAt)}
          </span>
        </div>

        {/* Details */}
        <div className="mt-3 space-y-1.5 text-xs text-[#4B5563]">
          <p className="m-0 flex items-center gap-2 min-w-0">
            <MapPin size={13} className="text-[#EA352B] shrink-0" /> <span className="truncate">{data.location}</span>
          </p>
          <p className="m-0 flex items-center gap-2">
            <Receipt size={13} className="text-[#EA352B] shrink-0" /> TRN <span className="font-mono text-[#111]">{data.vat}</span>
          </p>
          <p className="m-0 flex items-center gap-2">
            <FileCheck2 size={13} className="text-[#EA352B] shrink-0" /> {data.documents?.length || 0} documents uploaded
          </p>
        </div>

        {/* Services */}
        {data.services?.length > 0 && (
          <div className="flex gap-1.5 flex-wrap mt-3">
            {data.services.slice(0, 3).map((s) => (
              <span key={s} className="rounded-full bg-[#fff] border border-[#EEEEF1] px-2 py-0.5 text-[11px] text-[#4B5563]">{s}</span>
            ))}
            {data.services.length > 3 && <span className="text-[11px] text-[#9CA3AF] self-center">+{data.services.length - 3}</span>}
          </div>
        )}

        {/* Flags */}
        {(expired || expiringSoon) && status !== "rejected" && (
          <p className={`m-0 mt-3 inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-[11px] font-semibold ${expired ? "bg-[#FDECEA] text-[#EA352B]" : "bg-[#FEF3C7] text-[#B45309]"}`}>
            <AlertTriangle size={12} /> Trade license {expired ? "expired" : "expires soon"}
          </p>
        )}
        {status === "rejected" && data.rejectReason && (
          <p className="m-0 mt-3 rounded-xl bg-[#FFF5F4] border border-[#F6C9C5] px-3 py-2 text-[12px] text-[#991B1B]">
            <b className="font-semibold">Reason:</b> {data.rejectReason}
          </p>
        )}
      </button>

      {/* Actions */}
      <div className="flex items-center gap-2 px-4 py-3 border-t border-[#EEEEF1]">
        {status === "pending" && (
          <>
            <button type="button" onClick={onView} className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl border border-[#EEEEF1] bg-[#fff] py-2 text-xs font-semibold text-[#374151] hover:border-[#1B1B22] transition">
              <Eye size={14} /> Review
            </button>
            <button type="button" onClick={onReject} aria-label={`Reject ${data.title}`} title="Reject" className="w-9 h-9 rounded-xl bg-[#FDECEA] text-[#EA352B] flex items-center justify-center hover:bg-[#EA352B] hover:text-[#fff] transition">
              <X size={16} />
            </button>
            <button type="button" onClick={onApprove} aria-label={`Approve ${data.title}`} title="Approve" className="w-9 h-9 rounded-xl bg-[#16A34A] text-[#fff] flex items-center justify-center hover:bg-[#15803D] transition">
              <Check size={16} />
            </button>
          </>
        )}
        {status === "approved" && (
          <>
            <span className="flex-1 text-[11px] text-[#6B7280]">Approved {fromNow(data.reviewedAt)}</span>
            <button type="button" onClick={onView} className="inline-flex items-center gap-1.5 rounded-xl border border-[#EEEEF1] bg-[#fff] px-3 py-2 text-xs font-semibold text-[#374151] hover:border-[#1B1B22] transition">
              <Eye size={14} /> View documents
            </button>
          </>
        )}
        {status === "rejected" && (
          <>
            <button type="button" onClick={onView} className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl border border-[#EEEEF1] bg-[#fff] py-2 text-xs font-semibold text-[#374151] hover:border-[#1B1B22] transition">
              <Eye size={14} /> View
            </button>
            <button type="button" onClick={onApprove} className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl bg-[#1B1B22] py-2 text-xs font-semibold text-[#fff] hover:bg-[#000] transition">
              <RotateCcw size={13} /> Approve anyway
            </button>
          </>
        )}
      </div>
    </article>
  );
};

export default ApplicationCard;
